import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, onUnauthorized } from '../api/client'

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated'

interface AuthContextValue {
  status: AuthStatus
  login: (password: string) => Promise<boolean>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const queryClient = useQueryClient()

  useEffect(() => {
    // There's no dedicated "am I logged in" endpoint - GET /api/children is cheap and
    // requires the same session auth every other route does, so it doubles as the check.
    // A rejected fetch (network failure, not an HTTP error status) must still resolve the
    // check - otherwise status is stuck at 'checking' forever and the app hangs on "Loading…"
    // with no way to retry. Failing closed (treat it as unauthenticated) at least gets the
    // user to a login screen they can retry from.
    api.GET('/children')
      .then(({ response }) => {
        setStatus(response.status === 200 ? 'authenticated' : 'unauthenticated')
      })
      .catch(() => setStatus('unauthenticated'))
  }, [])

  useEffect(() => {
    return onUnauthorized(() => {
      setStatus('unauthenticated')
      queryClient.clear()
    })
  }, [queryClient])

  async function login(password: string): Promise<boolean> {
    const { response } = await api.POST('/session', { body: { password } })
    if (response.status === 204) {
      setStatus('authenticated')
      return true
    }
    return false
  }

  async function logout(): Promise<void> {
    await api.DELETE('/session')
    setStatus('unauthenticated')
    queryClient.clear()
  }

  return <AuthContext.Provider value={{ status, login, logout }}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
